import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, Toast, Popconfirm } from '@douyinfe/semi-ui';
import { IconEdit, IconPlus, IconQrCode, IconSearch, IconScan, IconSourceControl, IconSetting } from '@douyinfe/semi-icons';
import { IconChangelog } from '@douyinfe/semi-icons-lab';
import request from '../../utils/request';
import styles from './index.module.scss';
import { useNavigate } from 'react-router-dom';
import QRCodeGenerator from '../../components/QRCodeGenerator';

const WeixinPublic = () => {
    const navigate = useNavigate();
    const handleAddSubmit = () => {
        // 重置错误状态
        setAddFormErrors({});

        // 验证表单
        let errors = {};
        if (!addFormData.public_name) {
            errors.public_name = `请输入${addFormType === 8 ? '小程序' : '公众号'}名称`;
        }
        if (!addFormData.app_id) {
            errors.app_id = `请输入${addFormType === 8 ? '小程序' : ''}AppID`;
        }
        if (!addFormData.app_secret) {
            errors.app_secret = `请输入${addFormType === 8 ? '小程序' : ''}AppSecret`;
        }
        if ([1, 2].includes(addFormType) && !addFormData.origin_id) {
            errors.origin_id = '请输入原始ID';
        }

        if (Object.keys(errors).length > 0) {
            setAddFormErrors(errors);
            return;
        }

        // 提交表单
        handleAdd(addFormData);
    };

    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [searchKey, setSearchKey] = useState('');
    const [searchType, setSearchType] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const publicType = params.get('public_type');
        return publicType ? parseInt(publicType) : null;
    });
    const [addFormType, setAddFormType] = useState(8);
    const [addFormData, setAddFormData] = useState({
        public_type: 8,
        public_name: '',
        origin_id: '',
        app_id: '',
        app_secret: '',
        aes_key: ''
    });
    const [addFormErrors, setAddFormErrors] = useState({});

    const publicTypeMap = {
        1: '微信订阅号',
        2: '微信服务号',
        8: '微信小程序',
        1001: '小红书小程序',
        1002: '抖音小程序'
    };

    const accessModeMap = {
        0: '手动添加',
        1: '扫码授权'
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/weixin/list_weixin_public/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    public_type: searchType
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authUrl, setAuthUrl] = useState('');
    const [showRegisterBetaModal, setShowRegisterBetaModal] = useState(false);
    const [registerBetaData, setRegisterBetaData] = useState({
        name: '',
        openid: ''
    });
    const [registerBetaErrors, setRegisterBetaErrors] = useState({});
    const [showQrModal, setShowQrModal] = useState(false);
    const [qrUrl, setQrUrl] = useState('');

    const handleAuth = async () => {
        try {
            const response = await request.get('/go/sp/wxc/get_preauth_url/');
            if (response.errno === 0) {
                setAuthUrl(response.url);
                setShowAuthModal(true);
            } else {
                Toast.error(response.errstr || '获取授权链接失败');
            }
        } catch (error) {
            console.error('获取授权链接失败:', error);
            Toast.error('获取授权链接失败');
        }
    };

    const handleRegisterBeta = async () => {
        // 重置错误状态
        setRegisterBetaErrors({});

        // 验证表单
        let errors = {};
        if (!registerBetaData.name || registerBetaData.name.length < 4 || registerBetaData.name.length > 30) {
            errors.name = '小程序名称长度必须在4-30个字符之间';
        }
        if (!registerBetaData.openid) {
            errors.openid = '请输入小程序管理员OpenID';
        }

        if (Object.keys(errors).length > 0) {
            setRegisterBetaErrors(errors);
            return;
        }

        try {
            const response = await request.post('/go/sp/wxc/register_beta_xcx/?channel=k2', registerBetaData);
            if (response.errno === 0) {
                Toast.success('注册成功！请使用二维码进行授权确认');
                setShowRegisterBetaModal(false);
                setRegisterBetaData({ name: '', openid: '' });
                // 显示二维码
                setQrUrl(response.authorize_url);
                setShowQrModal(true);
            } else {
                Toast.error(response.tip_info || response.errstr || '注册失败');
            }
        } catch (error) {
            console.error('注册试用小程序失败:', error);
            Toast.error('注册失败');
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/weixin/add_weixin_public/', values);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/weixin/edit_weixin_public/', {
                ...values,
                uid: currentRecord.uid
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '名称/AppID',
            dataIndex: 'public_name',
            width: 200,
            render: (text, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text}</div>
                    <div style={{ color: 'var(--semi-color-text-2)' }}>{record.app_id}</div>
                </div>
            )
        },
        {
            title: '类型',
            dataIndex: 'public_type',
            width: 120,
            render: (text) => publicTypeMap[text] || '-'
        },
        {
            title: '授权模式',
            dataIndex: 'access_mod',
            width: 120,
            render: (text) => {
                const isScanAuth = text === 1; // 1 代表扫码授权
                return (
                    <span className={isScanAuth ? styles.scanAuth : ''}>
                        {accessModeMap[text] || '-'}
                    </span>
                );
            }
        },
        {
            title: '原始ID',
            dataIndex: 'origin_id',
            width: 180
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    {record.public_type === 8 && (
                        <Button
                            type='primary'
                            icon={<IconQrCode />}
                            onClick={() => {
                                navigate(`/qrcodegenerator?uid=${record.uid}`);
                            }}
                        >
                            小程序码
                        </Button>
                    )}
                    {record.public_type == 8 && record.access_mod == 1 && (
                        <Button
                            type='primary'
                            icon={<IconSourceControl />}
                            onClick={() => {
                                navigate(`/xcxversionmanager?uid=${record.uid}`);
                            }}
                        >
                            版本管理
                        </Button>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div className={styles.weixinPublicContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索名称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="类型"
                        value={searchType}
                        onChange={setSearchType}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部类型</Select.Option>
                        {Object.entries(publicTypeMap).map(([value, text]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button
                        icon={<IconPlus />}
                        onClick={() => setShowAddModal(true)}
                    >
                        手动添加
                    </Button>
                    <Button
                        icon={<IconScan />}
                        onClick={handleAuth}
                    >
                        扫码授权
                    </Button>
                    <Button
                        icon={<IconSetting />}
                        onClick={() => setShowRegisterBetaModal(true)}
                    >
                        注册试用小程序
                    </Button>
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
            />

            <Modal
                title="新增小程序"
                visible={showAddModal}
                onCancel={() => {
                    setShowAddModal(false);
                    setAddFormData({
                        public_type: 8,
                        public_name: '',
                        origin_id: '',
                        app_id: '',
                        app_secret: '',
                        aes_key: ''
                    });
                    setAddFormErrors({});
                }}
                footer={null}
                centered
            >
                <div style={{ padding: '0 0 24px' }}>
                    <div style={{ marginBottom: 16, color: 'var(--semi-color-text-2)', fontSize: 12 }}>
                        不知道如何填写相关参数，请查看 <a href="https://doc.weixin.qq.com/doc/w3_AIgAcwamAMA5xC0O7FET7q4DBymqr" target="_blank" rel="noopener noreferrer"><IconChangelog size="small"></IconChangelog>帮助文档</a>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8 }}>类型</div>
                        <Select
                            value={addFormData.public_type}
                            onChange={value => {
                                setAddFormData(prev => ({ ...prev, public_type: value }));
                                setAddFormType(value);
                            }}
                            placeholder="请选择类型"
                            style={{ width: '100%' }}
                        >
                            {Object.entries(publicTypeMap).map(([value, text]) => (
                                <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                            ))}
                        </Select>
                        {addFormErrors.public_type && (
                            <div style={{ color: 'var(--semi-color-danger)', fontSize: 12, marginTop: 4 }}>
                                {addFormErrors.public_type}
                            </div>
                        )}
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8 }}>{addFormType === 8 ? '小程序名称' : '公众号名称'} <span style={{ color: 'var(--semi-color-danger)' }}>*</span></div>
                        <Input
                            value={addFormData.public_name}
                            onChange={value => setAddFormData(prev => ({ ...prev, public_name: value }))}
                            placeholder={`请输入${addFormType === 8 ? '小程序' : '公众号'}名称`}
                        />
                        {addFormErrors.public_name && (
                            <div style={{ color: 'var(--semi-color-danger)', fontSize: 12, marginTop: 4 }}>
                                {addFormErrors.public_name}
                            </div>
                        )}
                    </div>

                    {[1, 2].includes(addFormType) && (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 8 }}>原始ID</div>
                            <Input
                                value={addFormData.origin_id}
                                onChange={value => setAddFormData(prev => ({ ...prev, origin_id: value }))}
                                placeholder="请输入原始ID"
                            />
                            {addFormErrors.origin_id && (
                                <div style={{ color: 'var(--semi-color-danger)', fontSize: 12, marginTop: 4 }}>
                                    {addFormErrors.origin_id}
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8 }}>{addFormType === 8 ? '小程序AppID' : 'AppID'} <span style={{ color: 'var(--semi-color-danger)' }}>*</span></div>
                        <Input
                            value={addFormData.app_id}
                            onChange={value => setAddFormData(prev => ({ ...prev, app_id: value }))}
                            placeholder={`请输入${addFormType === 8 ? '小程序' : ''}AppID`}
                        />
                        {addFormErrors.app_id && (
                            <div style={{ color: 'var(--semi-color-danger)', fontSize: 12, marginTop: 4 }}>
                                {addFormErrors.app_id}
                            </div>
                        )}
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8 }}>{addFormType === 8 ? '小程序AppSecret' : 'AppSecret'}</div>
                        <Input
                            value={addFormData.app_secret}
                            onChange={value => setAddFormData(prev => ({ ...prev, app_secret: value }))}
                            placeholder={`请输入${addFormType === 8 ? '小程序' : ''}AppSecret`}
                        />
                        {addFormErrors.app_secret && (
                            <div style={{ color: 'var(--semi-color-danger)', fontSize: 12, marginTop: 4 }}>
                                {addFormErrors.app_secret}
                            </div>
                        )}
                    </div>

                    {[1, 2].includes(addFormType) && (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 8 }}>AES Key</div>
                            <Input
                                value={addFormData.aes_key}
                                onChange={value => setAddFormData(prev => ({ ...prev, aes_key: value }))}
                                placeholder="请输入AES Key"
                            />
                        </div>
                    )}

                    <div style={{ marginTop: 24, textAlign: 'right' }}>
                        <Space>
                            <Button type='tertiary' onClick={() => {
                                setShowAddModal(false);
                                setAddFormData({
                                    public_type: 8,
                                    public_name: '',
                                    origin_id: '',
                                    app_id: '',
                                    app_secret: '',
                                    aes_key: ''
                                });
                                setAddFormErrors({});
                            }}>取消</Button>
                            <Button type='primary' onClick={handleAddSubmit}>确定</Button>
                        </Space>
                    </div>
                </div>
            </Modal>

            <Modal
                title={`编辑${publicTypeMap[currentRecord?.public_type] || '公众号'}`}
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
            >
                <Form
                    onSubmit={handleEdit}
                    initValues={{
                        public_name: currentRecord?.public_name,
                        origin_id: currentRecord?.origin_id,
                        app_id: currentRecord?.app_id,
                        app_secret: currentRecord?.app_secret || '',
                        aes_key: currentRecord?.aes_key
                    }}
                >
                    <Form.Input
                        field="public_name"
                        label={currentRecord?.public_type === 8 ? '小程序名称' : '公众号名称'}
                        placeholder={`请输入${currentRecord?.public_type === 8 ? '小程序' : '公众号'}名称`}
                        rules={[{ required: true, message: `请输入${currentRecord?.public_type === 8 ? '小程序' : '公众号'}名称` }]}
                    />
                    {[1, 2].includes(currentRecord?.public_type) && (
                        <Form.Input
                            field="origin_id"
                            label="原始ID"
                            placeholder="请输入原始ID"
                            rules={[{ required: true, message: '请输入原始ID' }]}
                            disabled={!!currentRecord?.origin_id}
                        />
                    )}
                    <Form.Input
                        field="app_id"
                        label={currentRecord?.public_type === 8 ? '小程序AppID' : 'AppID'}
                        placeholder={`请输入${currentRecord?.public_type === 8 ? '小程序' : ''}AppID`}
                        rules={[{ required: true, message: `请输入${currentRecord?.public_type === 8 ? '小程序' : ''}AppID` }]}
                        disabled={!!currentRecord?.app_id}
                    />
                    <Form.Input
                        field="app_secret"
                        label={currentRecord?.public_type === 8 ? '小程序AppSecret' : 'AppSecret'}
                        placeholder={`为了安全，已设置过的AppSecret将不再显示`}
                    />
                    {[1, 2].includes(currentRecord?.public_type) && (
                        <Form.Input
                            field="aes_key"
                            label="AES Key"
                            placeholder="请输入AES Key"
                        />
                    )}
                    <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
                        <Space>
                            <Button type='tertiary' onClick={() => setShowEditModal(false)}>取消</Button>
                            <Button type='primary' htmlType='submit'>确定</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            <Modal
                title="微信授权"
                visible={showAuthModal}
                onCancel={() => setShowAuthModal(false)}
                footer={null}
                width={800}
                height={600}
            >
                {authUrl && (
                    <iframe
                        src={authUrl}
                        width="100%"
                        height="500px"
                        frameBorder="0"
                        allowFullScreen
                    ></iframe>
                )}
            </Modal>

            <Modal
                title="注册试用小程序"
                visible={showRegisterBetaModal}
                onCancel={() => {
                    setShowRegisterBetaModal(false);
                    setRegisterBetaData({ name: '', openid: '' });
                    setRegisterBetaErrors({});
                }}
                footer={null}
                centered
            >
                <div style={{ padding: '0 0 24px' }}>
                    <div style={{ marginBottom: 16, color: 'var(--semi-color-text-2)', fontSize: 12 }}>
                        注册试用小程序后，管理员需要扫描二维码确认授权
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8 }}>小程序名称 <span style={{ color: 'var(--semi-color-danger)' }}>*</span></div>
                        <Input
                            value={registerBetaData.name}
                            onChange={value => setRegisterBetaData(prev => ({ ...prev, name: value }))}
                            placeholder="请输入小程序名称（4-30个字符）"
                            maxLength={30}
                        />
                        {registerBetaErrors.name && (
                            <div style={{ color: 'var(--semi-color-danger)', fontSize: 12, marginTop: 4 }}>
                                {registerBetaErrors.name}
                            </div>
                        )}
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8 }}>管理员OpenID <span style={{ color: 'var(--semi-color-danger)' }}>*</span></div>
                        <Input
                            value={registerBetaData.openid}
                            onChange={value => setRegisterBetaData(prev => ({ ...prev, openid: value }))}
                            placeholder="请输入小程序管理员OpenID"
                        />
                        {registerBetaErrors.openid && (
                            <div style={{ color: 'var(--semi-color-danger)', fontSize: 12, marginTop: 4 }}>
                                {registerBetaErrors.openid}
                            </div>
                        )}
                    </div>

                    <div style={{ marginTop: 24, textAlign: 'right' }}>
                        <Space>
                            <Button type='tertiary' onClick={() => {
                                setShowRegisterBetaModal(false);
                                setRegisterBetaData({ name: '', openid: '' });
                                setRegisterBetaErrors({});
                            }}>取消</Button>
                            <Button type='primary' onClick={handleRegisterBeta}>注册</Button>
                        </Space>
                    </div>
                </div>
            </Modal>

            <Modal
                title="授权确认"
                visible={showQrModal}
                onCancel={() => setShowQrModal(false)}
                footer={
                    <Button type='primary' onClick={() => setShowQrModal(false)}>
                        关闭
                    </Button>
                }
                centered
                width={400}
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{ marginBottom: 16, color: 'var(--semi-color-text-1)' }}>
                        请小程序管理员扫描下方二维码完成授权确认
                    </div>
                    {qrUrl && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                            <QRCodeGenerator
                                text={qrUrl}
                                size={200}
                                style={{ border: '1px solid var(--semi-color-border)' }}
                            />
                        </div>
                    )}
                    <div style={{ fontSize: 12, color: 'var(--semi-color-text-2)' }}>
                        扫码后按提示完成授权，系统将自动接收授权通知
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default WeixinPublic;